import type {
  TimelineEvent,
  TimelineEra,
  TimelinePerson,
  TimelineScene,
} from "@/hooks/useTimelineData";

type Props = {
  type: "event" | "era" | "person" | "scene";
  item: TimelineEvent | TimelineEra | TimelinePerson | TimelineScene;
  x: number;
  y: number;
  canEdit: boolean;
  onEdit: () => void;
  onViewFull: () => void;
};

const STATUS_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  draft: { bg: "bg-gray-500/15", text: "text-gray-400", label: "Draft" },
  approved: { bg: "bg-teal-500/15", text: "text-teal-400", label: "Approved" },
  killed: { bg: "bg-red-500/15", text: "text-red-400", label: "Killed" },
  shot: { bg: "bg-green-500/15", text: "text-green-400", label: "Shot" },
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "Unknown";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function HoverCard({ type, item, x, y, canEdit, onEdit, onViewFull }: Props) {
  const cardStyle: React.CSSProperties = {
    position: "fixed",
    left: Math.min(x + 12, window.innerWidth - 320),
    top: Math.min(y + 12, window.innerHeight - 220),
    zIndex: 100,
  };

  if (type === "event") {
    const event = item as TimelineEvent;
    return (
      <div
        style={cardStyle}
        className="bg-surface border border-border rounded-lg p-4 w-72 animate-in fade-in duration-100"
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="text-foreground font-medium text-sm leading-snug">
            {event.title}
          </h3>
          <span className="flex-shrink-0 px-1.5 py-0.5 bg-primary-500/10 text-primary-400 text-[10px] rounded">
            Event
          </span>
        </div>
        <p className="text-muted text-xs mb-2">{formatDate(event.date)}</p>
        {event.description && (
          <p className="text-muted text-xs leading-relaxed line-clamp-3">
            {event.description}
          </p>
        )}
        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onViewFull();
            }}
            className="px-2.5 py-1 bg-surface-elevated hover:bg-border text-muted text-xs rounded transition-colors"
          >
            View Full
          </button>
          {canEdit && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className="px-2.5 py-1 bg-surface-elevated hover:bg-border text-muted text-xs rounded transition-colors"
            >
              Edit
            </button>
          )}
        </div>
      </div>
    );
  }

  if (type === "era") {
    const era = item as TimelineEra;
    return (
      <div
        style={cardStyle}
        className="bg-surface border border-border rounded-lg p-4 w-72 animate-in fade-in duration-100"
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="text-foreground font-medium text-sm leading-snug">
            {era.title}
          </h3>
          <span
            className="flex-shrink-0 px-1.5 py-0.5 text-[10px] rounded"
            style={{
              backgroundColor: era.color + "15",
              color: era.color,
            }}
          >
            Era
          </span>
        </div>
        <p className="text-muted text-xs mb-2">
          {formatDate(era.startDate)} — {formatDate(era.endDate)}
        </p>
        {era.description && (
          <p className="text-muted text-xs leading-relaxed line-clamp-3">
            {era.description}
          </p>
        )}
        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onViewFull();
            }}
            className="px-2.5 py-1 bg-surface-elevated hover:bg-border text-muted text-xs rounded transition-colors"
          >
            View Full
          </button>
          {canEdit && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className="px-2.5 py-1 bg-surface-elevated hover:bg-border text-muted text-xs rounded transition-colors"
            >
              Edit
            </button>
          )}
        </div>
      </div>
    );
  }

  if (type === "person") {
    const person = item as TimelinePerson;
    return (
      <div
        style={cardStyle}
        className="bg-surface border border-border rounded-lg p-4 w-72 animate-in fade-in duration-100"
      >
        <div className="flex items-start gap-3 mb-2">
          {person.imageUrl ? (
            <img
              src={person.imageUrl}
              alt={person.name}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-surface-elevated flex items-center justify-center">
              <span className="text-muted text-sm font-medium">
                {person.name[0]}
              </span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-foreground font-medium text-sm">
              {person.name}
            </h3>
            <p className="text-muted text-xs">
              {formatDate(person.birthDate)}
              {person.deathDate
                ? ` — ${formatDate(person.deathDate)}`
                : " — Present"}
            </p>
          </div>
        </div>
        {person.bio && (
          <p className="text-muted text-xs leading-relaxed line-clamp-3">{person.bio}</p>
        )}
        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onViewFull();
            }}
            className="px-2.5 py-1 bg-surface-elevated hover:bg-border text-muted text-xs rounded transition-colors"
          >
            View Full
          </button>
        </div>
      </div>
    );
  }

  if (type === "scene") {
    const scene = item as TimelineScene;
    const statusStyle = STATUS_COLORS[scene.status] || STATUS_COLORS.draft;
    return (
      <div
        style={cardStyle}
        className="bg-surface border border-border rounded-lg p-4 w-72 animate-in fade-in duration-100"
      >
        <div className="flex items-start gap-3 mb-2">
          {scene.firstImageUrl ? (
            <img
              src={scene.firstImageUrl}
              alt={scene.title}
              className="w-12 h-12 rounded object-cover"
            />
          ) : (
            <div className="w-12 h-12 rounded bg-surface-elevated flex items-center justify-center">
              <svg className="w-6 h-6 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-primary-400 text-xs font-mono">{scene.sceneCode}</span>
              <span className={`px-1.5 py-0.5 ${statusStyle.bg} ${statusStyle.text} text-[10px] rounded`}>
                {statusStyle.label}
              </span>
            </div>
            <h3 className="text-foreground font-medium text-sm leading-snug">
              {scene.title}
            </h3>
          </div>
        </div>
        <p className="text-muted text-[11px] mt-2">
          Click to open in Scene Editor
        </p>
      </div>
    );
  }

  return null;
}
